import uniqWith from 'lodash/uniqWith';

import { startVirtualPackageDownloader, getVirtualPackageDownloader } from '@/tools/virtualPackageSyncer';
import { mutationTypes, actionTypes, errorTypes } from '@/types';
import { nativeMethodCall } from '@/tools/nativeInterface';

import { PACKAGE_SYNCER_MODES, syncActionStates } from '@/tools/consts';
import { isDataBaseID } from '@/tools/helpers';
import logger from '@/tools/logger';

const syncActionTypes = {
	download: syncActionStates.loadPending,
	delete: syncActionStates.deletePending,
};

function packageUniq(array) {
	return uniqWith(array, (x, y) => x.name === y.name && x.version === y.version);
}

function getPackagesGoal({ mode, id, state }) {
	switch (mode) {
		case PACKAGE_SYNCER_MODES.exhibition:
			return state.dataSourceCFG.exhibitionMap[id].packagesWithVersion;
		case PACKAGE_SYNCER_MODES.tour:
			return state.tour.toursMap[id].packagesWithVersion;
		case PACKAGE_SYNCER_MODES.all:
			return state.dataSourceCFG.exhibitions.reduce(
				(acc, exhibition) => [...acc, ...exhibition.packagesWithVersion],
				[]
			);
	}
}

function getMediaObjects({ mode, id, state }) {
	let mediaObjects =
		mode === PACKAGE_SYNCER_MODES.all
			? Object.values(state.dataSourceCFG.mediaObjectsInfo.exhibition).reduce(
					(acc, _mediaObjects) => [...acc, ..._mediaObjects],
					[]
			  )
			: state.dataSourceCFG.mediaObjectsInfo[mode][id];
	return mediaObjects || [];
}

let isSyncing = false;
let currentAction = null;
const actionsQueue = [];
let apiVersion = null;

function getPackageList(state, mode, id, currentLanguage) {
	const packagesGoal = getPackagesGoal({ mode, id, state });
	// If we have an old datasource config we fall back to returning non language specific
	if (!state.dataSourceCFG.mediaObjectsInfo) return packageUniq(packagesGoal);
	// Filter out media file ids from packagesGoal and return only media files that match the current language
	const mediaObjects = getMediaObjects({ mode, id, state });

	return packageUniq([
		...packagesGoal.filter(({ name }) => !name.startsWith('media-')),
		// Convert list into the `packagesWithVersion` format.
		...mediaObjects.map((mediaObjectId) => ({
			name: `media-${state.dataSourceCFG.mediaObjectsInfo.mediaObjects[mediaObjectId][currentLanguage]}`,
			version: 0,
		})),
	]);
}

export const appStartOfflineAppSync = (state, listOfApps) =>
	nativeMethodCall({
		method: 'nativeAppStartOfflineAppSync',
		params: [JSON.stringify(listOfApps)],
		fallback: () => (state.isVirtualPackageSyncer ? startVirtualPackageDownloader(listOfApps) : undefined),
	});

export const appGetOfflineAppsWithVersion = (state) => {
	return nativeMethodCall({
		method: 'nativeAppGetOfflineAppsWithVersion',
		resultTransform: (apps) => {
			return apps ? JSON.parse(apps) : null;
		},
		fallback: () =>
			state.isVirtualPackageSyncer
				? getVirtualPackageDownloader(state.dataSourceCFG.offlineAppWithVersionProviderInit)
				: undefined,
	});
};

export default {
	// Init action for packageSyncer is needed to detect if app is an package Syncer app
	// also gets current installed packages
	async [actionTypes.packageSyncerInit]({ commit, rootState, getters, dispatch }) {
		let packages = await appGetOfflineAppsWithVersion(rootState);
		const { mode } = rootState.dataSourceCFG.configWebClient.packageSyncer;
		// an result means it is an package Syncer app
		if (!packages) {
			logger.error('PackageSyncer is not initialised');
			return;
		}
		packages = (packages || []).map((pkg) => pkg.name);
		nativeMethodCall({
			method: 'nativeInfoGetAPIVersion',
			resultTransform: (result) => {
				apiVersion = parseInt(result, 10);
				if (apiVersion < 15) {
					dispatch(actionTypes.sendErrorMessage, {
						error: new Error(errorTypes.nativeApiVersionTooOld),
						sticky: true,
					});
				}
			},
		});
		if (mode === PACKAGE_SYNCER_MODES.exhibition) {
			const exhibitions = rootState.dataSourceCFG.exhibitions.filter((ex) => isDataBaseID(ex.exhibitionID));
			for (const exhibition of exhibitions) {
				const packageNames = getPackageList(
					rootState,
					mode,
					exhibition.exhibitionID,
					getters.currentLanguage
				).map((_package) => _package.name);
				const isPackageAvailable = packageNames.every((pkg) => packages.includes(pkg));
				if (isPackageAvailable) {
					commit(mutationTypes.packageSyncerSetSubPartState, {
						subPartId: exhibition.exhibitionID,
						subPartState: syncActionStates.loaded,
					});
				}
			}
		}
		if (mode === PACKAGE_SYNCER_MODES.tour) {
			const tours = rootState.dataSourceCFG.tours.filter((tour) => isDataBaseID(tour._id));
			for (const tour of tours) {
				const packageNames = getPackageList(rootState, mode, tour._id, getters.currentLanguage).map(
					(_package) => _package.name
				);
				const isPackageAvailable = packageNames.every((pkg) => packages.includes(pkg));
				if (isPackageAvailable) {
					commit(mutationTypes.packageSyncerSetSubPartState, {
						subPartId: tour._id,
						subPartState: syncActionStates.loaded,
					});
				}
			}
		}
		if (mode === PACKAGE_SYNCER_MODES.all) {
			const packageNames = getPackageList(rootState, mode, '', getters.currentLanguage).map(
				(_package) => _package.name
			);
			const isPackageAvailable = packageNames.every((pkg) => packages.includes(pkg));
			if (isPackageAvailable) {
				commit(mutationTypes.packageSyncerSetSubPartState, {
					subPartId: rootState.dataSourceCFG.global.landingExhibit.exhibitionID,
					subPartState: syncActionStates.loaded,
				});
			}
		}
		// Set all offline available packaget to the store so we can use them later
		commit(mutationTypes.setOfflineAvailablePackages, { packages });
		commit(mutationTypes.packageSyncerSetIsSyncerApp, { mode });
		// Loads all packages on startup
		if (rootState.isLoaner || mode === PACKAGE_SYNCER_MODES.all) {
			dispatch(actionTypes.openModal, { id: 'ui--loader' });
			dispatch(actionTypes.packageSyncerLoad, { subPartId: PACKAGE_SYNCER_MODES.all });
		}
		// load all packages of an exhibition
		const exhibitionMode =
			rootState.dataSourceCFG.exhibitionMap[rootState.dataSourceCFG.global.landingExhibit.exhibitionID]
				.configWebClient.packageSyncer.mode;
		if (exhibitionMode === PACKAGE_SYNCER_MODES.all && mode === PACKAGE_SYNCER_MODES.exhibition) {
			dispatch(actionTypes.openModal, { id: 'ui--loader' });
			dispatch(actionTypes.packageSyncerLoad, {
				subPartId: rootState.dataSourceCFG.global.landingExhibit.exhibitionID,
			});
		}
	},

	// Action downloads one subPart of all packages like one exhibition/tour
	// if exhibition or tour is downloaded is defined by pacakgeSyncer.mode
	async [actionTypes.packageSyncerLoad]({ state, dispatch }, { subPartId }) {
		// If the load is pending, was started, or is done do nothing.
		const subPartState = state.subPartStates[subPartId] || '';
		if (subPartState && subPartState !== syncActionStates.loadIncomplete) {
			return;
		}
		dispatch(actionTypes._packageSyncerStart, { subPartId, actionType: syncActionTypes.download });
	},

	// Delete a subpart
	async [actionTypes.packageSyncerDelete]({ state, dispatch }, { subPartId }) {
		// Only delete if the sub part was loaded before.
		if (state.subPartStates[subPartId] !== syncActionStates.loaded) return;
		dispatch(actionTypes._packageSyncerStart, { subPartId, actionType: syncActionTypes.delete });
	},

	// Start the next action from the `actionsQueue`.
	async [actionTypes._packageSyncerStart]({ state, rootState, commit, getters }, action) {
		if (action) {
			// if there is an action add it to the queue an mark it as waiting
			commit(mutationTypes.packageSyncerSetSubPartState, {
				subPartId: action.subPartId,
				subPartState: action.actionType, // waiting
			});
			actionsQueue.push(action);
		}
		if (isSyncing) return;
		currentAction = actionsQueue.pop(); // needed in packageSyncerStopped
		const { subPartId, actionType } = currentAction;

		const neededPackages = Object.entries(state.subPartStates)
			// filter all downloaded subparts except for the one we want to delete/load
			.filter(([id, subPartState]) => id !== subPartId && subPartState === syncActionStates.loaded)
			// flatmap all their packages together with the initalPackage list
			.reduce(
				(acc, [id]) => [...acc, ...getPackageList(rootState, state.mode, id, getters.currentLanguage)],
				rootState.dataSourceCFG.offlineAppWithVersionProviderInit
			);
		let targetPackages;
		// Start the sync process and set the state of the current subPart to loading
		// prettier-ignore
		if (actionType === syncActionTypes.download) {
			targetPackages = [...neededPackages, ...getPackageList(rootState, state.mode, subPartId, getters.currentLanguage)];
			commit(mutationTypes.packageSyncerSetSubPartState, { subPartId, subPartState: syncActionStates.loading });
		} else if (actionType === syncActionTypes.delete) {
			targetPackages = neededPackages;
			commit(mutationTypes.packageSyncerSetSubPartState, { subPartId, subPartState: syncActionStates.deleting });
		}
		if (apiVersion < 20) {
			appStartOfflineAppSync(rootState, packageUniq(targetPackages));
		} else {
			appStartOfflineAppSync(rootState, {
				syncOnlyLanguage: getters.currentLanguage,
				appsWithVersion: packageUniq(targetPackages),
			});
		}
		isSyncing = true;
	},

	// Action dispatched when packageSyncer stopped syncronisation
	async [actionTypes.packageSyncerStopped]({ rootState, state, commit, dispatch, getters }) {
		isSyncing = false;
		const { subPartId } = currentAction;

		// Check if there were errors during the sync process.
		if (state.errors.length > 0) {
			state.errors.forEach(({ message }) => logger.error(message));
			dispatch(actionTypes.sendErrorMessage, {
				error: new Error(
					currentAction.actionType === syncActionTypes.download
						? errorTypes.packageSyncerDownloadError
						: errorTypes.packageSyncerDeleteError
				),
			});
			commit(mutationTypes.packageSyncerClearErrors);
		}

		// Update the current list of offline available packages.
		commit(mutationTypes.setOfflineAvailablePackages, {
			packages: await appGetOfflineAppsWithVersion(rootState),
		});

		// Compare package list if the download was completed or if the delete was sucessfull
		const targetPackages = getPackageList(rootState, state.mode, subPartId, getters.currentLanguage);
		const offlineTargetPackages = targetPackages.filter(({ name }) =>
			state.offlineAvailablePackagesIndex.has(name)
		);

		// Set the new state of the subpart that was processed to loaded, inclomplete
		// or if detleted ot null (so it can be downloaded again)
		if (offlineTargetPackages.length >= targetPackages.length) {
			// loaded
			commit(mutationTypes.packageSyncerSetSubPartState, {
				subPartId,
				subPartState: syncActionStates.loaded,
			});
			if (state.mode == PACKAGE_SYNCER_MODES.exhibition) {
				dispatch(actionTypes.setExhibition, { exhibitionID: subPartId });
			}
		} else if (
			currentAction.actionType === syncActionTypes.download &&
			offlineTargetPackages.length < targetPackages.length
		) {
			const missingPackages = targetPackages
				.filter(({ name }) => !offlineTargetPackages.some((item) => item.name === name))
				.map(({ name }) => name);
			logger.error('Packages that were not downloaded: ', missingPackages);
			// incomplete
			commit(mutationTypes.packageSyncerSetSubPartState, {
				subPartId,
				subPartState: syncActionStates.loadIncomplete,
			});
		} else {
			// deleted
			commit(mutationTypes.packageSyncerSetSubPartState, { subPartId, subPartState: null });
		}
		// Update the local storage with the current state for the next session
		localStorage.selectedSubParts = JSON.stringify(
			Object.entries(state.subPartStates)
				.filter(([, subPartState]) => subPartState === syncActionStates.loaded)
				.reduce((acc, [subPartId]) => Object.assign(acc, { [subPartId]: getters.currentLanguage }), {})
		);

		currentAction = null;
		if (actionsQueue.length && state.errors.length === 0) {
			dispatch(actionTypes._packageSyncerStart);
		}
	},
};
